import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Download,
  FileText,
  Pencil,
  Trash2,
  Plus,
  ArrowLeft,
  MoreHorizontal,
  Eye,
  CheckCircle,
  Search,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from "sonner";
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { getAllInsights, deleteInsight, updateInsightStatus } from '@/services/insight';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { insightsStatusOptions, pagination } from '@/constants';
import { capitalizeWords } from '@/helper';

const MarketInsights = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const fetchInsights = async (page = pagination.page) => {
    setIsLoading(true);
    try {
      const { data, pagination: paginationData } = await getAllInsights({
        page: page,
        limit: pagination.limit,
        searchQuery: debouncedSearchQuery,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });

      if (Array.isArray(data)) {
        setReports(data);
        if (paginationData) {
          setTotalPages(paginationData?.totalPages);
        }
      } else {
        console.error("Unexpected API response format:", data);
      }
    } catch (error) {
      console.error("Failed to fetch insights:", error);
      toast.error("Failed to load market insights");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights(); // Always fetch page 1 on filter change
  }, [debouncedSearchQuery, statusFilter]);

  // Handle report deletion
  const handleDeleteReport = (report: any) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      const identifier = reportToDelete.slug || reportToDelete._id;
      await deleteInsight(identifier);
      setReports(reports.filter(r => r._id !== reportToDelete._id));
      toast.success("Report deleted successfully");
    } catch (error) {
      console.error("Failed to delete report:", error);
      toast.error("Failed to delete report");
    } finally {
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  // Handle editing a report
  const handleEditReport = (report: any) => {
    if (report.slug) {
      navigate(`/admin/market-insights/edit/${report.slug}`);
    } else {
      toast.error("Cannot edit report without a slug");
    }
  };

  const handleStatusUpdate = async (slug: string, status: string) => {
    try {
      await updateInsightStatus(slug, status);
      toast.success(`Report marked as ${status}`);
      fetchInsights();
    } catch (error: any) {
      console.error("Error updating report status:", error);
      toast.error(error.response?.data?.error || "Failed to update report status");
    }
  };

  const statusColors: { [key: string]: string } = {
    published: 'bg-green-100 text-green-800 hover:bg-green-100',
    draft: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    scheduled: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  };




  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="pt-32 pb-20 bg-secondary/30 flex-1">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Market Insights</h1>
              <p className="text-muted-foreground">Dubai real estate market analysis and trends</p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline">
                <Link to="/admin/dashboard">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Dashboard
                </Link>
              </Button>
              <Button asChild>
                <Link to="/admin/market-insights/create">
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Report
                </Link>
              </Button>
            </div>
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            {/* ... (keep commented out tabs list and content) ... */}

            {/* Market Reports Tab - New management interface */}
            <TabsContent value="reports" className="space-y-6">

              <Card className="mb-8">
                <CardHeader className="pb-3">
                  <CardTitle>Filters</CardTitle>
                  <CardDescription>Search and filter blog posts</CardDescription>
                </CardHeader>
                <CardContent>

                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search by title or category..."
                        className="pl-8"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full md:w-[200px]">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {insightsStatusOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                        {/* <SelectItem value="scheduled">Scheduled</SelectItem> */}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Report</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      {/* <TableHead>Size</TableHead> */}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      [1, 2, 3, 4, 5].map((i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Skeleton className="h-8 w-8 rounded-md" />
                              <div className="space-y-1">
                                <Skeleton className="h-4 w-48" />
                                <Skeleton className="h-3 w-64" />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                        </TableRow>
                      ))
                    ) : reports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No reports found matching your filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.map((report) => (
                        <TableRow key={report._id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-primary" />
                              <div>
                                <div className="font-medium">{capitalizeWords(report.title)}</div>
                                <div className="text-sm text-muted-foreground line-clamp-1">{report.description}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{new Date(report.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</TableCell>
                          <TableCell>{capitalizeWords(report.category)}</TableCell>
                          <TableCell>
                            <Badge className={statusColors[report.status || 'draft'] || 'bg-gray-100'}>
                              {capitalizeWords(report.status || 'draft')}
                            </Badge>
                          </TableCell>
                          {/* <TableCell>{report.fileSize}</TableCell> */}
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem asChild>
                                  <Link to={`/market-insights/${report.slug}`}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    View
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <a href={report.pdfFile || '#'} target="_blank" rel="noopener noreferrer" download>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Report
                                  </a>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditReport(report)}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {report.status !== 'published' && (
                                  <DropdownMenuItem onClick={() => handleStatusUpdate(report.slug, 'published')}>
                                    <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                    Mark as Published
                                  </DropdownMenuItem>
                                )}
                                {report.status !== 'draft' && (
                                  <DropdownMenuItem onClick={() => handleStatusUpdate(report.slug, 'draft')}>
                                    <FileText className="h-4 w-4 mr-2 text-gray-600" />
                                    Mark as Draft
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteReport(report)}>
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>

              {/* Pagination */}
              <div className="flex justify-center mt-4">
                <PaginationControl
                  key={`${debouncedSearchQuery}-${statusFilter}`} // Reset on filter change
                  totalPages={totalPages}
                  onPageChange={fetchInsights}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this report? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div >
  );
};

export default MarketInsights;
